/**
 * Where is this widget actually running?
 *
 * In a Shopify admin the answer is layered: the merchant is on
 * `admin.shopify.com`, which iframes the app, which loads us. A cross-origin
 * child cannot read its parent's URL, so the top-level origin has to be
 * inferred, and the inference is not equally reliable everywhere:
 *
 *   1. `location.ancestorOrigins` — exact, ordered, but Chromium/WebKit only.
 *   2. `document.referrer` — present on the first load of a framed document;
 *      gives an origin but not the full chain, and can be suppressed by a
 *      referrer policy.
 *   3. Nothing — Firefox with a strict referrer policy legitimately leaves us
 *      unable to tell. We report `null` rather than guessing.
 *
 * None of this is a security boundary. The server independently records the
 * `Origin` header, which the browser sets and a page cannot forge, and that is
 * what the app's allowlist is actually checked against.
 */

function safe(fn, fallback = null) {
  try {
    return fn();
  } catch {
    // Cross-origin property access throws rather than returning undefined.
    return fallback;
  }
}

function originOf(url) {
  try {
    return new URL(url).origin;
  } catch {
    return null;
  }
}

/**
 * Shopify hands every embedded app a `host` query parameter: base64url of
 * `admin.shopify.com/store/<store-handle>`. It is on *our* frame's URL, which
 * we can read, so it survives the referrer policy that strips everything else.
 *
 * It yields the store's admin base rather than the exact page, so it is
 * reported as reconstructed — never as exact.
 */
function shopifyAdminBase() {
  const host = safe(() => new URL(window.location.href).searchParams.get('host'), null);
  if (!host) return null;

  const decoded = safe(() => {
    const normalized = host.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4);
    return window.atob(padded);
  }, null);

  // Expect `admin.shopify.com/store/<handle>`; anything else is not ours.
  if (!decoded || !/^[a-z0-9.-]+\/store\/[A-Za-z0-9._-]+/.test(decoded)) return null;
  return `https://${decoded}`;
}

export function collectContext(overrides = {}) {
  const embedded = safe(() => window.self !== window.top, true);

  // Outermost last, matching how the browser orders it.
  const ancestorOrigins = safe(
    () => Array.from(window.location.ancestorOrigins ?? []).slice(0, 10),
    []
  );

  const referrer = safe(() => document.referrer, '') ?? '';

  let topOrigin = null;
  if (!embedded) {
    topOrigin = window.location.origin;
  } else if (ancestorOrigins.length > 0) {
    topOrigin = ancestorOrigins[ancestorOrigins.length - 1];
  } else if (referrer) {
    // One level up at best — better than nothing, and flagged as inferred.
    topOrigin = originOf(referrer);
  }

  /**
   * The full top-level URL, with the path and query the origin alone omits.
   *
   * Only readable outright when we are the top document or same-origin with
   * it. Cross-origin, the referrer is the best available answer — but note it
   * is the *immediate parent's* URL, which in a nested embed is not the top
   * document. The source is recorded alongside so the dashboard can say which
   * of those an agent is looking at instead of implying certainty.
   */
  let topUrl = '';
  let topUrlSource = 'unknown';

  const sameOrigin = embedded ? safe(() => window.top.location.href, null) : null;
  const adminBase = shopifyAdminBase();

  if (overrides.topUrl) {
    // The host page told us outright. It is the only party that can know the
    // exact admin URL, so this wins over every inference below.
    topUrl = overrides.topUrl;
    topUrlSource = 'provided';
  } else if (!embedded) {
    topUrl = window.location.href;
    topUrlSource = 'direct';
  } else if (sameOrigin) {
    // Cross-origin access throws; reaching here means the embed is same-origin.
    topUrl = sameOrigin;
    topUrlSource = 'direct';
  } else if (adminBase) {
    // Beats the referrer under a strict referrer policy, which collapses it to
    // a bare origin and takes the path with it.
    topUrl = adminBase;
    topUrlSource = 'host-param';
  } else if (referrer) {
    topUrl = referrer;
    // In a nested chain the referrer is the parent frame, not the top.
    topUrlSource = ancestorOrigins.length > 1 ? 'referrer-parent' : 'referrer';
  }

  return {
    origin: window.location.origin,
    topOrigin: topOrigin ?? '',
    topUrl: topUrl.slice(0, 2000),
    topUrlSource,
    embedded,
    ancestorOrigins,
    referrer: referrer.slice(0, 2000),
    language: safe(() => navigator.language, '') ?? '',
    timezone: safe(() => Intl.DateTimeFormat().resolvedOptions().timeZone, '') ?? '',
    viewport: `${window.innerWidth} × ${window.innerHeight}`,
    url: currentUrl(),
    title: safe(() => document.title, '')?.slice(0, 300) ?? '',
  };
}

/**
 * The widget's own page, in full — query string included, as requested.
 *
 * Worth knowing: admin URLs frequently carry session tokens or HMACs in the
 * query, and these are persisted and shown to agents. That is a deliberate
 * choice here, not an oversight; drop `search` below to go back to path-only.
 */
export function currentUrl() {
  const { origin, pathname, search, hash } = window.location;
  return `${origin}${pathname}${search}${hash}`.slice(0, 2000);
}

/**
 * Reports route changes in the host's single-page admin.
 *
 * `pushState`/`replaceState` fire no event, so they are wrapped. Batched
 * through a short debounce because SPA routers commonly replace state several
 * times per navigation, and each of those is not a page view.
 */
export function trackNavigation(onChange, { debounceMs = 800 } = {}) {
  let lastUrl = currentUrl();
  let timer = null;

  const report = () => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      const url = currentUrl();
      if (url === lastUrl) return;
      lastUrl = url;
      onChange({ url, title: safe(() => document.title, '')?.slice(0, 300) ?? '' });
    }, debounceMs);
  };

  const { pushState, replaceState } = window.history;

  const wrap = (original) =>
    function wrapped(...args) {
      const result = original.apply(this, args);
      report();
      return result;
    };

  window.history.pushState = wrap(pushState);
  window.history.replaceState = wrap(replaceState);
  window.addEventListener('popstate', report);
  window.addEventListener('hashchange', report);

  return function stop() {
    clearTimeout(timer);
    // Restore the originals — leaving a wrapper behind after teardown would
    // keep this widget's closure alive for the life of the host page.
    window.history.pushState = pushState;
    window.history.replaceState = replaceState;
    window.removeEventListener('popstate', report);
    window.removeEventListener('hashchange', report);
  };
}

export default { collectContext, trackNavigation, currentUrl };
